import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Loader2 } from "lucide-react";

import OperationsShell from "@/components/operations/OperationsShell";
import TodoWorkspace from "@/components/todo/TodoWorkspace";
import { api, formatApiError } from "@/lib/api";
import { humaniseSalesActionType } from "@/lib/salesActionLabel";
import { formatHumanDate } from "@/lib/humanDate";

/**
 * DEV-026 · Sales "To-do" workspace.
 *
 * Presents two clearly-labelled sections:
 *
 *   1. Ad-hoc to-do items (SHARED workspace via `TodoWorkspace`)
 *      backed by the ordinary `POST /tasks` endpoint. Same underlying
 *      record as the Tasks workspace — a to-do IS an ad-hoc task.
 *
 *   2. Project Sales actions (existing `projects.sales_actions[]`
 *      sub-array). Preserved model + completion behaviour; rendered
 *      under a distinct heading so operators can tell them apart
 *      from ad-hoc items.
 */
export default function SalesTodo() {
    return (
        <OperationsShell>
            <TodoWorkspace
                eyebrow="SALES PORTAL"
                subtitle="Your ad-hoc to-do list and project sales actions."
                testidPrefix="sales-todo"
                additionalSection={<SalesActionsSection />}
            />
        </OperationsShell>
    );
}


function SalesActionsSection() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [completingId, setCompletingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await api.listSalesActions("outstanding");
            setRows(Array.isArray(r?.actions) ? r.actions : []);
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || "Failed to load sales actions");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    async function handleComplete(row) {
        setCompletingId(row.sales_action_id);
        try {
            await api.completeSalesAction(row.project_id, row.sales_action_id);
            toast.success("Sales action completed");
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to complete");
        } finally {
            setCompletingId(null);
        }
    }

    if (loading) return <div className="mt-8 text-xs text-[#4B5565]" data-testid="sales-todo-actions-loading">Loading sales actions…</div>;
    if (error) return <div className="mt-8 text-xs text-[#B91C1C]" data-testid="sales-todo-actions-error">{error}</div>;
    if (rows.length === 0) return null;

    return (
        <section className="mt-10" data-testid="sales-todo-actions-section">
            <div className="mb-3 flex items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#4B5565] font-medium">SALES ACTIONS</div>
                    <div className="text-sm font-semibold text-[#131B2F]">Project sales follow-ups</div>
                </div>
                <div className="text-[11px] text-[#4B5565]">Separate from ad-hoc to-dos above.</div>
            </div>
            <ul className="space-y-2" data-testid="sales-todo-actions-list">
                {rows.map((r) => {
                    const humanTitle = humaniseSalesActionType(r.action_type) || r.action_type || "Sales action";
                    const humanDue = r.due_date ? formatHumanDate(r.due_date) : "";
                    return (
                        <li
                            key={r.sales_action_id}
                            data-testid={`sales-todo-action-${r.sales_action_id}`}
                            className="bg-white rounded-xl border border-[#E5E7EB] p-4 flex items-start gap-3"
                        >
                            <div className="w-9 h-9 rounded-md bg-[#FEF3C7] text-[#B45309] flex items-center justify-center shrink-0">
                                <CheckCircle2 size={18} strokeWidth={1.75} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="text-sm font-semibold text-[#131B2F] truncate">
                                        {humanTitle}
                                    </div>
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#B45309]">
                                        Sales action
                                    </span>
                                </div>
                                <div className="mt-0.5 text-xs text-[#4B5565]">
                                    {r.customer_name || "—"}{r.project_address ? ` · ${r.project_address}` : ""}
                                </div>
                                <div className="mt-2 flex items-center justify-between gap-3">
                                    <span className="text-[11px] text-[#4B5565]">{humanDue ? `Due ${humanDue}` : "No due date"}</span>
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to={`/sales/projects/${r.project_id}?section=sales-actions`}
                                            className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                                        >
                                            <ExternalLink size={12} /> Open project
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={() => handleComplete(r)}
                                            disabled={completingId === r.sales_action_id}
                                            data-testid={`sales-todo-action-complete-${r.sales_action_id}`}
                                            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-[#3E9E2E] text-white text-xs font-semibold hover:bg-[#348426] disabled:opacity-60"
                                        >
                                            {completingId === r.sales_action_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                                            Mark complete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
